// PhysioSewa - Service Categories (6 cards from clinic pamphlet)

export const CONDITIONS = [
  {
    slug: "pain-management",
    icon: "fa-bolt",
    title: "Pain Management",
    summary: "Back pain, knee pain, frozen shoulder & more.",
    what: [
      "Pain Management at PhysioSewa addresses chronic and acute musculoskeletal pain — from everyday back and neck stiffness to debilitating sciatica, frozen shoulder and cervical spondylosis. Our physiotherapists conduct a thorough assessment to identify the root cause, not just the symptom.",
      "We combine manual therapy, modalities and targeted exercise to deliver fast, drug-free pain relief and prevent recurrence.",
    ],
    symptoms: [
      "Back & Neck Pain",
      "Knee Pain & Osteoarthritis",
      "Frozen Shoulder",
      "Slipped Disc & Sciatica",
      "Cervical Pain",
      "Postural Pain",
    ],
    treatment: [
      { title: "Assessment & root-cause diagnosis", desc: "Hands-on evaluation to find the true source of your pain." },
      { title: "Manual therapy + targeted modalities", desc: "Mobilisation, dry needling, ultrasound and IFT for fast relief." },
      { title: "Strengthening & relapse prevention", desc: "Personalised exercise and ergonomic guidance for long-term results." },
    ],
    recovery: "4–8 weeks for most conditions",
  },
  {
    slug: "neurological-rehabilitation",
    icon: "fa-brain",
    title: "Neurological Rehabilitation",
    summary: "Stroke, Parkinson's, balance & coordination.",
    what: [
      "Our neurological rehabilitation programme helps patients recover movement, balance and independence after stroke, Parkinson's disease, facial palsy, vertigo and other neurological conditions. We use evidence-based, task-specific training to maximise functional recovery.",
      "Each programme is patient-centred and progresses in measurable milestones with full carer involvement.",
    ],
    symptoms: [
      "Stroke Rehabilitation",
      "Parkinson's Disease Rehab",
      "Balance & Co-ordination Training",
      "Gait Training",
      "Facial & Bell's Palsy",
      "Cerebral Palsy",
      "Vertigo",
    ],
    treatment: [
      { title: "Neurological assessment & goal setting", desc: "Detailed baseline testing and shared, realistic recovery goals." },
      { title: "Task-specific movement & gait training", desc: "Practising the exact daily activities you need to regain." },
      { title: "Balance work + carer education", desc: "Family is trained to safely support continued progress at home." },
    ],
    recovery: "8–16 weeks of consistent therapy for meaningful improvement",
  },
  {
    slug: "geriatric-rehabilitation",
    icon: "fa-person-cane",
    title: "Geriatric Rehabilitation",
    summary: "Age-related joint pain, mobility & fall prevention.",
    what: [
      "Geriatric rehabilitation at PhysioSewa is dedicated to helping older adults stay active, independent and pain-free. From age-related joint pain and reduced mobility to fall prevention and Alzheimer's support — our therapists deliver gentle, respectful, results-driven care.",
      "We offer both clinic and home visit options to suit elderly patients and their families across Gurugram.",
    ],
    symptoms: [
      "Age-Related Joint Pain",
      "Mobility Improvement",
      "Fall Prevention",
      "Joint Stiffness",
      "Musculo-Skeletal Pain",
      "Cardio Vascular Pain",
      "Alzheimer's Disease",
    ],
    treatment: [
      { title: "Comprehensive elderly assessment", desc: "Mobility, strength, balance and cognitive screening." },
      { title: "Gentle joint mobilisation & strengthening", desc: "Carefully graded exercise tailored for older adults." },
      { title: "Fall prevention & home safety education", desc: "Practical strategies to keep you safe and confident at home." },
    ],
    recovery: "6–12 weeks for most conditions, with ongoing support available",
  },
  {
    slug: "orthopaedic-sports-rehabilitation",
    icon: "fa-person-running",
    title: "Orthopaedic & Sports Rehabilitation",
    summary: "Fractures, sports injuries, post-surgical rehab.",
    what: [
      "From weekend warriors to professional athletes — our orthopaedic and sports rehabilitation programme helps you recover from fractures, sports injuries and major surgeries, and return stronger than before.",
      "We work closely with your surgeon's protocol to ensure safe, structured progression at every stage of recovery.",
    ],
    symptoms: [
      "Post Fracture Rehabilitation",
      "Post-Surgical Rehab (Knee, Shoulder & Spine)",
      "Sports Injury Management",
      "Ligament & Tendon Injuries",
      "Post TKR & Post THR Rehabilitation",
    ],
    treatment: [
      { title: "Acute pain & swelling management", desc: "Surgeon-approved care in the early healing phase." },
      { title: "Progressive strength & range-of-motion", desc: "Carefully graded loading to rebuild tissue and confidence." },
      { title: "Sports-specific return-to-play", desc: "Functional drills tailored to your sport or daily life." },
    ],
    recovery: "6–16 weeks depending on injury or surgery",
  },
  {
    slug: "maternity-care",
    icon: "fa-person-pregnant",
    title: "Maternity Care",
    summary: "Pre & post-natal physiotherapy & core care.",
    what: [
      "Maternity physiotherapy at PhysioSewa supports women through pregnancy, childbirth and postnatal recovery in a confidential, respectful environment. From pregnancy exercises and posture care to LSCS recovery and core strengthening — we are with you at every step.",
      "Our therapists are trained in pelvic-floor and women's health rehabilitation.",
    ],
    symptoms: [
      "Post-Natal & Pre-Natal Care",
      "Pregnancy Exercises",
      "Posture & Breathing Exercises",
      "LSCS After Care",
      "Core Strengthening",
    ],
    treatment: [
      { title: "Confidential maternity assessment", desc: "Safe, respectful evaluation tailored to your stage." },
      { title: "Targeted pregnancy & post-natal exercises", desc: "Pelvic floor, posture and core work that's safe and effective." },
      { title: "Long-term self-management education", desc: "Tools to maintain results well beyond delivery." },
    ],
    recovery: "6–12 weeks postnatal recovery; ongoing pregnancy support",
  },
  {
    slug: "manual-therapy",
    icon: "fa-hands",
    title: "Manual Therapy",
    summary: "Joint mobilization, dry needling & cupping.",
    what: [
      "Manual Therapy at PhysioSewa combines hands-on joint and soft-tissue techniques with modern adjuncts like dry needling and cupping. Used alongside exercise therapy, manual therapy can deliver fast, targeted relief that exercise alone often cannot achieve.",
      "All techniques are evidence-based and selected for your specific condition and recovery stage.",
    ],
    symptoms: [
      "Joint Mobilization",
      "Soft Tissue Mobilization",
      "MFR (Myofascial Release)",
      "Cupping Therapy",
      "Dry Needling Therapy",
    ],
    treatment: [
      { title: "Targeted technique selection", desc: "The right hands-on tool for your specific tissue and condition." },
      { title: "Combined with active exercise", desc: "Manual work paired with movement for lasting change." },
      { title: "Suitable for", desc: "Back pain, sports injuries, post-surgical rehab, chronic pain and tendinopathy." },
    ],
    recovery: "Applied as part of a comprehensive treatment plan",
  },
];

export const getCondition = (slug) => CONDITIONS.find(c => c.slug === slug);

export const getRelated = (slug, n = 3) => {
  const others = CONDITIONS.filter(c => c.slug !== slug);
  const seed = slug.split("").reduce((a, b) => a + b.charCodeAt(0), 0);
  return others.sort((a, b) => ((a.slug.charCodeAt(0) + seed) % 7) - ((b.slug.charCodeAt(0) + seed) % 7)).slice(0, n);
};
